/**
 * Simulation Results API service.
 * @module simulationResultsApi
 */

import { apiGet, apiPost } from './apiUtils';

export interface ApiModuleResult {
    module: string;
    is_success: boolean;
    passed: boolean;
    score: number;
    is_graded: boolean;
    created_at: string;
    analysis_status?: 'pending' | 'running' | 'succeeded' | 'failed' | null;
    analysis_error_code?: string | null;
    analysis_attempts?: number | null;
    analysis_can_retry?: boolean | null;
    analysis_updated_at?: string | null;
    // Diagnostic Feedback System — aggregated per-section LLM feedback status
    // for R/L modules. Detailed per-section text comes from a separate endpoint.
    section_feedback_status?: 'pending' | 'complete' | 'failed' | null;
}

export interface ApiSimulationSession {
    sessionId: string;
    createdAt: string;
    examType: string;
    level: string;
    totalScore: number;
    maxTotalScore: number;
    percentage: number;
    passed: boolean;
    modules: ApiModuleResult[];
}

export interface ApiAnalysisStatusResponse {
    session_id: string;
    module: string;
    analysis_status: 'pending' | 'running' | 'succeeded' | 'failed';
    analysis_error_code?: string | null;
    analysis_attempts: number;
    analysis_can_retry: boolean;
    analysis_updated_at?: string | null;
}

/**
 * Get all simulation history for a specific user.
 */
export async function getAllPreviousResults(userId: string): Promise<ApiSimulationSession[]> {
    const result = await apiGet<ApiSimulationSession[]>(
        `/simulation/results/user/${userId}/all`,
        {
            errorMessage: 'Failed to fetch simulation results',
            context: 'getAllPreviousResults',
            errorReturnValue: [],
        },
    );
    return result ?? [];
}

/**
 * Get filtered simulation history for a specific user.
 */
export async function getPreviousResults(
    userId: string,
    level: string,
    examType: string
): Promise<ApiSimulationSession[]> {
    const result = await apiGet<ApiSimulationSession[]>(
        `/simulation/results/user/${userId}/level/${level}/${examType}`,
        {
            errorMessage: 'Failed to fetch filtered simulation results',
            context: 'getPreviousResults',
            errorReturnValue: [],
        },
    );
    return result ?? [];
}

/**
 * Get detailed answers for a specific module in a session.
 */
export async function getModuleAnswers(
    sessionId: string,
    module: string
): Promise<any[]> {
    const result = await apiGet<any[]>(
        `/simulation/results/session/${sessionId}/module/${module}/answers`,
        {
            errorMessage: 'Failed to fetch module answers',
            context: 'getModuleAnswers',
            errorReturnValue: [],
        },
    );
    return result ?? [];
}

/**
 * Retry AI analysis for one module in a simulation session.
 */
export async function retryModuleAnalysis(
    sessionId: string,
    module: string,
): Promise<ApiAnalysisStatusResponse> {
    const result = await apiPost<ApiAnalysisStatusResponse>(
        `/simulation/results/session/${sessionId}/module/${module}/analysis/retry`,
        undefined,
        {
            errorMessage: 'Failed to retry module analysis',
            context: 'retryModuleAnalysis',
            throwOnError: true,
        },
    );
    if (!result) {
        throw new Error('Failed to retry module analysis');
    }
    return result;
}

/**
 * Per-section LLM feedback for one R/L module (Diagnostic Feedback System).
 */
export interface ApiRecommendedPractice {
    readonly title: string;
    readonly id: string;
}

export interface ApiSectionFeedback {
    readonly section: number;
    readonly status: 'pending' | 'complete' | 'failed';
    readonly strength_summary?: string | null;
    readonly weakness_summary?: string | null;
    readonly notes_summary?: string | null;
    readonly priority_focus?: string | null;
    readonly recommended_practice?: readonly ApiRecommendedPractice[];
    readonly updated_at?: string | null;
}

export async function getModuleSectionFeedback(
    sessionId: string,
    module: string,
): Promise<ApiSectionFeedback[]> {
    const result = await apiGet<ApiSectionFeedback[]>(
        `/simulation/results/session/${sessionId}/module/${module}/section-feedback`,
        {
            errorMessage: 'Failed to fetch section feedback',
            context: 'getModuleSectionFeedback',
            errorReturnValue: [],
        },
    );
    return result ?? [];
}
